// Slide-over panneau d'édition : liste des widgets du catalogue avec
// toggle on/off, et flèches up/down pour réordonner les actifs.
// Pas de D&D en V1 (L3-bis).
import { ChevronDown, ChevronUp, X } from "lucide-react";

export default function WidgetLibrarySheet({
  open, onClose, catalog = [], layout, onToggle, onMove,
}) {
  if (!open) return null;

  const widgets = layout?.widgets || [];
  const widgetMap = Object.fromEntries(widgets.map((w) => [w.id, w]));

  // Tri : actifs en premier (par ordre), puis inactifs par titre
  const active = widgets
    .filter((w) => w.enabled !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const inactiveIds = new Set(active.map((w) => w.id));
  const inactive = catalog
    .filter((c) => !inactiveIds.has(c.id))
    .sort((a, b) => a.title.localeCompare(b.title));

  return (
    <div
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
      aria-label="Bibliothèque de widgets"
      data-testid="widget-library-sheet"
    >
      <button
        type="button"
        aria-label="Fermer le panneau"
        onClick={onClose}
        className="flex-1"
        style={{ background: "rgba(0,0,0,0.5)" }}
      />
      <aside
        className="w-full max-w-md h-full overflow-y-auto"
        style={{
          background: "var(--oh-bg)",
          borderLeft: "1px solid var(--oh-border)",
        }}
      >
        <header
          className="sticky top-0 px-4 py-3 flex items-center justify-between"
          style={{
            background: "var(--oh-bg-elevated)",
            borderBottom: "1px solid var(--oh-border)",
          }}
        >
          <h2 className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>
            Bibliothèque de widgets
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="p-1 rounded"
            style={{ color: "var(--oh-text)" }}
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-4 space-y-5">
          <section>
            <p
              className="text-xs uppercase tracking-wider mb-2"
              style={{ color: "var(--oh-text-muted)" }}
            >
              Widgets actifs ({active.length})
            </p>
            {active.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>
                Aucun widget actif.
              </p>
            ) : (
              <ul className="space-y-2">
                {active.map((w, idx) => {
                  const meta = catalog.find((c) => c.id === w.id);
                  return (
                    <li
                      key={w.id}
                      className="flex items-center gap-2 p-2 rounded-lg"
                      style={{
                        background: "var(--oh-bg-elevated)",
                        border: "1px solid var(--oh-border)",
                      }}
                    >
                      <div className="flex flex-col gap-0.5">
                        <button
                          type="button"
                          onClick={() => onMove?.(w.id, "up")}
                          disabled={idx === 0}
                          aria-label="Monter"
                          className="disabled:opacity-30"
                          style={{ color: "var(--oh-text)" }}
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onMove?.(w.id, "down")}
                          disabled={idx === active.length - 1}
                          aria-label="Descendre"
                          className="disabled:opacity-30"
                          style={{ color: "var(--oh-text)" }}
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: "var(--oh-text)" }}>
                          {meta?.title || w.id}
                        </p>
                        {meta?.description && (
                          <p className="text-xs truncate" style={{ color: "var(--oh-text-muted)" }}>
                            {meta.description}
                          </p>
                        )}
                      </div>
                      <label className="inline-flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={true}
                          onChange={() => onToggle?.(w.id)}
                          aria-label={`Activer ${meta?.title || w.id}`}
                          data-testid={`widget-toggle-${w.id}`}
                        />
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section>
            <p
              className="text-xs uppercase tracking-wider mb-2"
              style={{ color: "var(--oh-text-muted)" }}
            >
              Widgets disponibles ({inactive.length})
            </p>
            {inactive.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>
                Tous les widgets sont actifs.
              </p>
            ) : (
              <ul className="space-y-2">
                {inactive.map((meta) => (
                  <li
                    key={meta.id}
                    className="flex items-center gap-2 p-2 rounded-lg"
                    style={{
                      background: "var(--oh-bg-surface)",
                      border: "1px solid var(--oh-border)",
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: "var(--oh-text)" }}>
                        {meta.title}
                      </p>
                      <p className="text-xs truncate" style={{ color: "var(--oh-text-muted)" }}>
                        {meta.description}
                      </p>
                      <span
                        className="text-[10px] uppercase tracking-wider"
                        style={{ color: "var(--oh-text-muted)" }}
                      >
                        {meta.category} · {meta.default_size}
                      </span>
                    </div>
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={() => onToggle?.(meta.id)}
                        aria-label={`Activer ${meta.title}`}
                        data-testid={`widget-toggle-${meta.id}`}
                      />
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}
